import RailsEnv from '@frontend/app/features/RailsEnv';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {
  searchBox,
  infiniteHits,
  sortBy,
  refinementList,
  panel,
} from 'instantsearch.js/es/widgets';

import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    searchQuery: {
      type: String,
      default: '',
    },
    timerId: {
      type: Number,
      default: 0,
    },
    debounceTimeout: {
      type: Number,
      default: 500,
    },
    indexName: {
      type: String,
      default: 'Article_development',
    },
    indexNameForAlternatingSorting: String,
    replicaForAlternatingSortingPrefix: {
      type: String,
      default: 'articles_alternate_sort',
    },
    appId: String,
    apiKey: String,
  };

  static targets = ['inputContainer'];

  connect() {
    this.search = instantsearch({
      indexName: this.indexNameValue,
      searchClient: algoliasearch(this.appIdValue, this.apiKeyValue),
      future: {
        preserveSharedStateOnUnmount: true,
      },
    });

    this.setupWidgets();
    this.search.start();
    this.setupSearchInput();
  }

  setupWidgets() {
    this.search.addWidgets([
      searchBox({
        container: '#search-box',
        showReset: false,
        showSubmit: false,
        placeholder: 'Archiv durchsuchen',
        queryHook: this.handleQueryHook.bind(this),
      }),
      sortBy({
        container: '#sort-by',
        items: [
          { label: 'Relevanz', value: this.indexNameValue },
          {
            label: 'Veröffentlichungsdatum',
            value: this.indexNameForAlternatingSortingValue,
          },
        ],
      }),
      infiniteHits({
        container: '#hits',
        templates: {
          showMoreText(data, { html }) {
            return html`<span>Mehr laden</span>`;
          },
          item: (hit, { html, components }) => html`
            <article class="algolia-search-index__search-result">
              <a
                class="algolia-search-index__search-result-topic-link"
                href="https://krautreporter.de/thema/${hit.topic_slug}"
              >
                ${hit.topic_title}
              </a>
              <h2 class="algolia-search-index__search-result-headline">
                <a
                  class="algolia-search-index__search-result-link"
                  href="https://krautreporter.de/${hit.objectID}-"
                  target="_blank"
                >
                  ${components.Highlight({ attribute: 'title', hit })}
                </a>
              </h2>
              <div class="algolia-search-index__search-result-metadata">
                <a
                  class="algolia-search-index__search-result-author-link"
                  href="https://krautreporter.de/${hit.author_slug}"
                >
                  ${hit.author_name}
                </a>
                <span class="algolia-search-index__search-result-published-date"
                  >• ${hit.published_date}</span
                >
              </div>
              <p>${components.Snippet({ hit, attribute: 'teaser' })}</p>
            </article>
          `,
        },
      }),
      panel()(refinementList)({
        container: '#author-list',
        attribute: 'author_name',
        limit: 10,
        showMore: true,
        templates: {
          showMoreText: this.renderShowMoreButton,
        },
      }),
      panel()(refinementList)({
        container: '#format-list',
        attribute: 'format_title',
        limit: 10,
        showMore: false,
      }),
    ]);
  }

  renderShowMoreButton(data, { html }) {
    return html`<span>${data.isShowingMore ? 'Weniger' : 'Mehr'}</span>`;
  }

  handleQueryHook(query, refine) {
    clearTimeout(this.timerIdValue);
    this.timerIdValue = setTimeout(
      () => refine(query),
      this.debounceTimeoutValue
    );
  }

  indexNameValueChanged() {
    this.indexNameForAlternatingSortingValue = [
      this.replicaForAlternatingSortingPrefixValue,
      RailsEnv.getEnv(),
    ].join('_');
  }

  setupSearchInput() {
    this.searchInput =
      this.inputContainerTarget.querySelector('input[type=search]');
    if (this.searchQueryValue.length > 0) {
      this.searchInput.value = this.searchQueryValue;
      this.searchInput.dispatchEvent(new Event('input'));
    }
    this.searchInput.addEventListener('change', this.updateUrl.bind(this));
  }

  updateUrl() {
    if (history) {
      const query = encodeURIComponent(this.searchInput.value);
      let querySuffix = '';
      if (query.length > 0) {
        querySuffix = `?search_query=${query}`;
      }
      history.replaceState({ query }, '', `${location.pathname}${querySuffix}`);
    }
  }
}
